/**
 * DeliveryFlowSettingsPage — `/settings/delivery-flow` (epic #2437, #2454):
 * the tenant's delivery flow, the rows, columns and gates of the logistics
 * board.
 *
 * Six sections, laid out like `/settings/tenant` (#2429): template, batch,
 * columns, actions, gates, integrations. Template, columns and actions are
 * read-only and come from `GET /api/delivery-flow` in the order it gives (the
 * actions drawn as a state → action → state chain, #2501); integrations is
 * static copy. The board range, the rest days and the three gates are edited
 * row by row through `PUT /api/tenant/settings` (#2449) — the save model stays
 * row by row. A save refreshes the delivery flow, so `/dispatch` and the board
 * preview in the right-hand column (#2501) read the new values at once.
 *
 * A thin shell: every effective value comes from the backend, which has
 * already fallen back to its defaults. Admin-only by route (`<AdminGuard>`)
 * and server-side. The time zone is read-only (it has no write path).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, PageShell } from '@resource-kit/ui/react';
import { useDeliveryFlow } from '@features/dispatch';
import { useTenantBranding } from '@hooks/useTenantBranding';
import type { TenantSettings } from '@/services/tenants';
import { useTenantSettings } from '../hooks/useTenants';
import { SettingsPanel, SettingsRow, SettingsSection } from '../components/SettingsSection';
import { BoardRangeRow, RestWeekdaysRow } from '../components/DeliveryFlowCalendar';
import { DeliveryFlowGateRows } from '../components/DeliveryFlowGateRows';
import { DeliveryFlowPreview } from '../components/DeliveryFlowPreview';
import { actionChain } from '../components/deliveryFlowModel';
import {
  ACTION_KEY,
  COLUMN_KEY,
  EXECUTOR_KEY,
  type DeliveryFlow,
} from '../components/deliveryFlowShared';

const SECTIONS = [
  {
    id: 'template',
    titleKey: 'deliveryFlow.sections.template.title',
    descriptionKey: 'deliveryFlow.sections.template.description',
  },
  {
    id: 'batch',
    titleKey: 'deliveryFlow.sections.batch.title',
    descriptionKey: 'deliveryFlow.sections.batch.description',
  },
  {
    id: 'columns',
    titleKey: 'deliveryFlow.sections.columns.title',
    descriptionKey: 'deliveryFlow.sections.columns.description',
  },
  {
    id: 'actions',
    titleKey: 'deliveryFlow.sections.actions.title',
    descriptionKey: 'deliveryFlow.sections.actions.description',
  },
  {
    id: 'gates',
    titleKey: 'deliveryFlow.sections.gates.title',
    descriptionKey: 'deliveryFlow.sections.gates.description',
  },
  {
    id: 'integrations',
    titleKey: 'deliveryFlow.sections.integrations.title',
    descriptionKey: 'deliveryFlow.sections.integrations.description',
  },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

const TEMPLATE_KEY = {
  hgs_same_day: {
    name: 'deliveryFlow.template.hgs_same_day',
    description: 'deliveryFlow.templateDescription.hgs_same_day',
  },
} as const satisfies Record<DeliveryFlow['template'], { name: string; description: string }>;

const BATCH_KEY = {
  shipping_date: 'deliveryFlow.batch.shipping_date',
} as const satisfies Record<DeliveryFlow['batch'], string>;

/** Who moves things on outside the system (#2501): static copy, no endpoint. */
const INTEGRATION_KEY = {
  orders: {
    label: 'deliveryFlow.integration.orders',
    value: 'deliveryFlow.integration.ordersValue',
  },
  delivery: {
    label: 'deliveryFlow.integration.delivery',
    value: 'deliveryFlow.integration.deliveryValue',
  },
  writeback: {
    label: 'deliveryFlow.integration.writeback',
    value: 'deliveryFlow.integration.writebackValue',
  },
} as const;

/** Settings / delivery flow, then the tenant once its name has loaded. */
function DeliveryFlowEyebrow() {
  const { t } = useTranslation();
  const { data } = useTenantBranding();
  return (
    <p data-testid="delivery-flow-eyebrow" className="text-xs text-muted-foreground">
      {[`${t('nav.management')} / ${t('deliveryFlow.page.title')}`, data?.name]
        .filter(Boolean)
        .join(' · ')}
    </p>
  );
}

export default function DeliveryFlowSettingsPage() {
  const { t } = useTranslation();
  const flowQuery = useDeliveryFlow();
  const settingsQuery = useTenantSettings();
  const flow = flowQuery.data;
  const settings = settingsQuery.data;

  const failed = flowQuery.isError || settingsQuery.isError;
  const loading = flowQuery.isLoading || settingsQuery.isLoading;
  const status = failed ? (
    <p className="text-sm text-destructive">{t('deliveryFlow.loadError')}</p>
  ) : loading ? (
    <p className="text-sm text-muted-foreground">{t('tenants.loading')}</p>
  ) : null;

  const content = (render: (flow: DeliveryFlow, settings: TenantSettings) => ReactNode) =>
    flow && settings ? render(flow, settings) : status;

  const sections: Record<SectionId, ReactNode> = {
    template: content((flow) => (
      <SettingsPanel>
        <SettingsRow
          label={t('deliveryFlow.row.template')}
          action={<Badge variant="success">{t('deliveryFlow.templateInUse')}</Badge>}
        >
          <div className="space-y-0.5">
            <p className="font-medium">{t(TEMPLATE_KEY[flow.template].name)}</p>
            <p className="text-muted-foreground">{t(TEMPLATE_KEY[flow.template].description)}</p>
          </div>
        </SettingsRow>
      </SettingsPanel>
    )),
    batch: content((flow, settings) => (
      <SettingsPanel>
        <SettingsRow label={t('deliveryFlow.row.batch')}>{t(BATCH_KEY[flow.batch])}</SettingsRow>
        <BoardRangeRow flow={flow} settings={settings} />
        <RestWeekdaysRow flow={flow} settings={settings} />
      </SettingsPanel>
    )),
    columns: content((flow) => (
      <SettingsPanel>
        {flow.columns.map((column) => (
          <SettingsRow
            key={column}
            testId={`delivery-flow-column-${column}`}
            label={t(COLUMN_KEY[column].name)}
          >
            {t(COLUMN_KEY[column].rule)}
          </SettingsRow>
        ))}
      </SettingsPanel>
    )),
    actions: content((flow) => (
      <SettingsPanel>
        <div data-testid="delivery-flow-chain" className="flex items-stretch overflow-x-auto p-4">
          {actionChain(flow.actions).map((item, index) =>
            item.kind === 'state' ? (
              <div
                key={`state-${index}`}
                data-testid={`delivery-flow-state-${item.stage}`}
                className="min-w-[92px] flex-none rounded-lg bg-muted px-3 py-2 text-sm font-medium"
              >
                {t(COLUMN_KEY[item.stage].name)}
              </div>
            ) : (
              <div
                key={`action-${item.action.action}`}
                data-testid={`delivery-flow-action-${item.action.action}`}
                className="relative flex min-w-[120px] flex-1 items-center justify-center px-1.5"
              >
                <span aria-hidden="true" className="absolute inset-x-1 top-1/2 border-t" />
                <div className="relative flex flex-col items-center gap-1 rounded-md bg-card px-1.5 py-0.5 text-center text-xs">
                  <span className="font-semibold">{t(ACTION_KEY[item.action.action].name)}</span>
                  <Badge variant="secondary">{t(EXECUTOR_KEY[item.action.executor])}</Badge>
                  <span className="text-muted-foreground">
                    {t(ACTION_KEY[item.action.action].signal)}
                  </span>
                </div>
              </div>
            ),
          )}
        </div>
      </SettingsPanel>
    )),
    gates: content((flow, settings) => (
      <SettingsPanel>
        <SettingsRow testId="delivery-flow-row-timezone" label={t('deliveryFlow.row.timezone')}>
          <span className="font-mono text-sm">{flow.timezone}</span>
        </SettingsRow>
        <DeliveryFlowGateRows flow={flow} settings={settings} />
      </SettingsPanel>
    )),
    integrations: (
      <SettingsPanel>
        {Object.entries(INTEGRATION_KEY).map(([row, key]) => (
          <SettingsRow key={row} testId={`delivery-flow-integration-${row}`} label={t(key.label)}>
            {t(key.value)}
          </SettingsRow>
        ))}
      </SettingsPanel>
    ),
  };

  return (
    <PageShell
      data-testid="delivery-flow-page"
      title={t('deliveryFlow.page.headline')}
      subtitle={t('deliveryFlow.page.subtitle')}
      breadcrumb={<DeliveryFlowEyebrow />}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-10">
          {SECTIONS.map((section) => (
            <SettingsSection
              key={section.id}
              id={section.id}
              title={t(section.titleKey)}
              description={t(section.descriptionKey)}
            >
              {sections[section.id]}
            </SettingsSection>
          ))}
        </div>
        {flow ? (
          <div className="lg:sticky lg:top-6 lg:self-start">
            <DeliveryFlowPreview flow={flow} />
          </div>
        ) : null}
      </div>
    </PageShell>
  );
}
